// frontend/src/pages/ProyectosGanttPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import eventosService from '../services/eventosService';
import GanttChart from '../components/GanttChart';
import EventoModal from '../components/EventoModal';
import './ProyectosGanttPage.css';

import Loading from '../components/Loading';
const STATUS_LABELS = {
  draft:       { label: 'Borrador',        color: '#6b7280', bg: '#f3f4f6' },
  planning:    { label: 'Planificación',   color: '#8b5cf6', bg: '#ede9fe' },
  confirmed:   { label: 'Confirmado',      color: '#3b82f6', bg: '#dbeafe' },
  in_progress: { label: 'En Progreso',     color: '#f59e0b', bg: '#fef3c7' },
  completed:   { label: 'Completado',      color: '#10b981', bg: '#d1fae5' },
  cancelled:   { label: 'Cancelado',       color: '#ef4444', bg: '#fee2e2' },
  postponed:   { label: 'Pospuesto',       color: '#9ca3af', bg: '#f9fafb' },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_LABELS[status] || { label: status, color: '#6b7280', bg: '#f3f4f6' };
  return (
    <span style={{
      background: cfg.bg, color: cfg.color,
      padding: '2px 8px', borderRadius: 12,
      fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap'
    }}>{cfg.label}</span>
  );
};

const ProyectosGanttPage = () => {
  const [proyectos, setProyectos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buscar, setBuscar] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [showCrearModal, setShowCrearModal] = useState(false);

  const cargar = useCallback(async () => {
    try {
      setLoading(true);
      const res = await eventosService.getEventos({ tipo_evento: 'proyecto' });
      const data = Array.isArray(res.data) ? res.data : (res.data.results || []);
      setProyectos(data);
      // Auto-seleccionar el primero si no hay selección
      if (data.length > 0 && !selectedId) {
        setSelectedId(data[0].id);
      }
    } catch {
      toast.error('Error al cargar los proyectos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const filtrados = proyectos.filter(p => {
    if (filtroStatus && p.status !== filtroStatus) return false;
    if (buscar && !p.title.toLowerCase().includes(buscar.toLowerCase())) return false;
    return true;
  });

  const selected = proyectos.find(p => p.id === selectedId);

  const formatDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  return (
    <div className="pg-page">
      {/* ── Header ── */}
      <div className="pg-header">
        <div className="pg-header-left">
          <h1 className="pg-title">📊 Gantt de Proyectos</h1>
          <span className="pg-subtitle">{proyectos.length} proyecto{proyectos.length !== 1 ? 's' : ''}</span>
        </div>
        <button className="pg-btn-primary" onClick={() => setShowCrearModal(true)}>
          + Nuevo Proyecto
        </button>
      </div>

      <div className="pg-layout">
        {/* ── Panel izquierdo: lista ── */}
        <aside className="pg-sidebar">
          {/* Filtros */}
          <div className="pg-sidebar-filters">
            <input
              className="pg-search"
              placeholder="Buscar proyecto..."
              value={buscar}
              onChange={e => setBuscar(e.target.value)}
            />
            <select
              className="pg-select-filter"
              value={filtroStatus}
              onChange={e => setFiltroStatus(e.target.value)}
            >
              <option value="">Todos los estados</option>
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>

          {/* Lista de proyectos */}
          <div className="pg-project-list">
            {loading ? (
              <Loading fullScreen={false} />
            ) : filtrados.length === 0 ? (
              <div className="pg-empty">
                {proyectos.length === 0
                  ? 'No hay proyectos. Crea uno con el botón de arriba.'
                  : 'No hay proyectos que coincidan con el filtro.'}
              </div>
            ) : filtrados.map(p => (
              <div
                key={p.id}
                className={`pg-project-card ${selectedId === p.id ? 'selected' : ''}`}
                onClick={() => setSelectedId(p.id)}
              >
                <div className="pg-card-top">
                  <span className="pg-card-title">{p.title}</span>
                  <StatusBadge status={p.status} />
                </div>
                {p.proyecto_nombre && (
                  <div className="pg-card-meta">📁 {p.proyecto_nombre}</div>
                )}
                {p.cliente_nombre && (
                  <div className="pg-card-meta">🏢 {p.cliente_nombre}</div>
                )}
                <div className="pg-card-dates">
                  <span>{formatDate(p.fecha_inicio)}</span>
                  {p.fecha_termino && <span>→ {formatDate(p.fecha_termino)}</span>}
                </div>
              </div>
            ))}
          </div>
        </aside>

        {/* ── Panel derecho: Gantt ── */}
        <main className="pg-gantt-panel">
          {!selectedId || !selected ? (
            <div className="pg-gantt-empty">
              <div className="pg-gantt-empty-icon">📊</div>
              <div className="pg-gantt-empty-text">Selecciona un proyecto para ver su Gantt</div>
            </div>
          ) : (
            <div className="pg-gantt-wrapper">
              {/* Cabecera del proyecto seleccionado */}
              <div className="pg-gantt-header">
                <div className="pg-gantt-header-info">
                  <h2 className="pg-gantt-title">{selected.title}</h2>
                  <div className="pg-gantt-meta-row">
                    <StatusBadge status={selected.status} />
                    {selected.proyecto_nombre && <span className="pg-gantt-meta">📁 {selected.proyecto_nombre}</span>}
                    {selected.cliente_nombre && <span className="pg-gantt-meta">🏢 {selected.cliente_nombre}</span>}
                    <span className="pg-gantt-meta">📅 {formatDate(selected.fecha_inicio)}{selected.fecha_termino ? ` → ${formatDate(selected.fecha_termino)}` : ''}</span>
                  </div>
                </div>
              </div>
              {/* Gantt con CRUD completo */}
              <GanttChart key={selectedId} eventoId={selectedId} />
            </div>
          )}
        </main>
      </div>

      {/* ── Modal crear proyecto ── */}
      {showCrearModal && (
        <EventoModal
          evento={{ tipo_evento: 'proyecto' }}
          onClose={() => {
            setShowCrearModal(false);
            cargar();
          }}
        />
      )}
    </div>
  );
};

export default ProyectosGanttPage;
